import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Button,
  Alert,
  ProgressBar,
  Modal,
  Spinner,
  Badge,
} from "react-bootstrap";
import { FaArrowLeft, FaCheck, FaClock } from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../components/common/Header";
import TestService from "../../services/testService";
import "../../styles/take-test.css";

const TakeTest = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();

  const [testData, setTestData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [startedAt, setStartedAt] = useState(null);  // ← ADD THIS LINE (FIX)

  useEffect(() => {
    loadTest();
  }, [attemptId]);

  // Timer logic - Fixed to work with startedAt
  useEffect(() => {
    if (timeRemaining > 0 && timeRemaining !== null) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleAutoSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeRemaining]);

  const loadTest = async () => {
    try {
      setLoading(true);
      const response = await TestService.getTestProgress(attemptId);
      console.log("Test progress response:", response.data);

      if (response.data) {
        const data = response.data;
        setTestData(data);

        const questionsArray = data.questions || [];
        console.log("Questions loaded:", questionsArray.length);
        setQuestions(questionsArray);

        const savedAnswers = data.saved_answers || {};
        setAnswers(savedAnswers);

        // Use remaining_seconds from server response
        if (
          data.remaining_seconds !== undefined &&
          data.remaining_seconds !== null
        ) {
          console.log("Remaining seconds from server:", data.remaining_seconds);
          setTimeRemaining(data.remaining_seconds);
        } else if (data.test && data.test.duration_minutes) {
          // Fallback to duration if no remaining time
          setTimeRemaining(data.test.duration_minutes * 60);
        }

        // Store started_at for reference
        if (data.started_at) {
          setStartedAt(data.started_at);
        }
      }
    } catch (error) {
      console.error("Failed to load test:", error);
      toast.error(error.response?.data?.message || "Failed to load test");
      navigate("/student/tests");
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSubmit = async () => {
    toast.warning("Time is up! Submitting your test...");
    await handleSubmit();
  };

  const handleAnswer = (questionId, selectedOption) => {
    const newAnswers = { ...answers, [questionId]: selectedOption };
    setAnswers(newAnswers);

    // Auto-save answer (don't wait for response)
    TestService.saveAnswer(attemptId, newAnswers).catch((err) => {
      console.error("Failed to save answer:", err);
    });
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      console.log("Submitting test with answers:", answers);

      const response = await TestService.submitTest(attemptId, answers);

      console.log("Submit response:", response.data);

      if (response.data && response.data.success !== false) {
        toast.success(response.data?.message || "Test submitted successfully");
        navigate(`/student/test-result/${attemptId}`);
      } else {
        throw new Error(response.data?.message || "Submission failed");
      }
    } catch (error) {
      console.error("Failed to submit test:", error);
      console.error("Error response:", error.response);
      console.error("Error data:", error.response?.data);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to submit test";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
      setShowSubmitModal(false);
    }
  };

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const getProgressPercent = () => {
    if (questions.length === 0) return 0;
    const answeredCount = Object.keys(answers).length;
    return (answeredCount / questions.length) * 100;
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading test...</p>
        </div>
      </div>
    );
  }

  if (!questions || questions.length === 0) {
    return (
      <div>
        <Header />
        <Container className="py-5 text-center">
          <Alert variant="danger">
            <h5>No questions available for this test</h5>
            <p>Please contact your instructor.</p>
            <Button
              variant="primary"
              onClick={() => navigate("/student/tests")}
              className="mt-3"
            >
              Back to Tests
            </Button>
          </Alert>
        </Container>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div>
      <Header />
      <div className="take-test-page">
        <Container className="py-4">
          {/* Header with Timer */}
          <div className="test-header">
            <div className="test-header-left">
              <Button
                variant="link"
                className="text-decoration-none"
                onClick={() => navigate("/student/tests")}
              >
                <FaArrowLeft className="me-2" /> Back to Tests
              </Button>
              <h4 className="mb-0 mt-2">{testData?.test?.title || "Test"}</h4>
            </div>
            <div className="test-header-right">
              <div className="timer-clock">
                <FaClock className="me-2" />
                Time Remaining:{" "}
                <strong className={timeRemaining < 300 ? "text-danger" : ""}>
                  {formatTime(timeRemaining)}
                </strong>
              </div>
            </div>
          </div>

          {/* Progress */}
          <div className="test-progress mb-4">
            <div className="d-flex justify-content-between mb-1">
              <small>
                Progress: {Object.keys(answers).length} of {questions.length}{" "}
                answered
              </small>
              <small>{Math.round(getProgressPercent())}%</small>
            </div>
            <ProgressBar now={getProgressPercent()} variant="primary" />
          </div>

          {/* Question Card */}
          {currentQuestion && (
            <Card className="question-card mb-4">
              <Card.Body>
                <div className="question-header mb-3">
                  <h5>
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </h5>
                  <Badge bg="secondary">
                    {currentQuestion.marks || 1} mark(s)
                  </Badge>
                </div>

                <div className="question-text mb-4">
                  <p
                    dangerouslySetInnerHTML={{
                      __html: currentQuestion.question,
                    }}
                  />
                </div>

                <div className="options-list">
                  <div
                    className={`option-item ${answers[currentQuestion.id] === "a" ? "selected" : ""}`}
                    onClick={() => handleAnswer(currentQuestion.id, "a")}
                  >
                    <div className="option-radio">
                      {answers[currentQuestion.id] === "a" ? <FaCheck /> : "A"}
                    </div>
                    <div
                      className="option-text"
                      dangerouslySetInnerHTML={{
                        __html: currentQuestion.option_a || "",
                      }}
                    />
                  </div>
                  <div
                    className={`option-item ${answers[currentQuestion.id] === "b" ? "selected" : ""}`}
                    onClick={() => handleAnswer(currentQuestion.id, "b")}
                  >
                    <div className="option-radio">
                      {answers[currentQuestion.id] === "b" ? <FaCheck /> : "B"}
                    </div>
                    <div
                      className="option-text"
                      dangerouslySetInnerHTML={{
                        __html: currentQuestion.option_b || "",
                      }}
                    />
                  </div>
                  <div
                    className={`option-item ${answers[currentQuestion.id] === "c" ? "selected" : ""}`}
                    onClick={() => handleAnswer(currentQuestion.id, "c")}
                  >
                    <div className="option-radio">
                      {answers[currentQuestion.id] === "c" ? <FaCheck /> : "C"}
                    </div>
                    <div
                      className="option-text"
                      dangerouslySetInnerHTML={{
                        __html: currentQuestion.option_c || "",
                      }}
                    />
                  </div>
                  <div
                    className={`option-item ${answers[currentQuestion.id] === "d" ? "selected" : ""}`}
                    onClick={() => handleAnswer(currentQuestion.id, "d")}
                  >
                    <div className="option-radio">
                      {answers[currentQuestion.id] === "d" ? <FaCheck /> : "D"}
                    </div>
                    <div
                      className="option-text"
                      dangerouslySetInnerHTML={{
                        __html: currentQuestion.option_d || "",
                      }}
                    />
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Navigation Buttons */}
          <div className="navigation-buttons d-flex justify-content-between">
            <Button
              variant="outline-secondary"
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
            >
              Previous
            </Button>
            <div>
              {currentQuestionIndex === questions.length - 1 ? (
                <Button
                  variant="success"
                  onClick={() => setShowSubmitModal(true)}
                  disabled={submitting}
                >
                  Submit Test
                </Button>
              ) : (
                <Button variant="primary" onClick={handleNext}>
                  Next
                </Button>
              )}
            </div>
          </div>

          {/* Question Palette */}
          <Card className="mt-4">
            <Card.Body>
              <h6 className="mb-3">Question Palette</h6>
              <div className="question-palette">
                {questions.map((q, idx) => (
                  <button
                    key={q.id}
                    className={`palette-btn ${idx === currentQuestionIndex ? "current" : ""} ${
                      answers[q.id] ? "answered" : "unanswered"
                    }`}
                    onClick={() => setCurrentQuestionIndex(idx)}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
              <div className="palette-legend mt-3">
                <span>
                  <span className="legend-box answered"></span> Answered
                </span>
                <span>
                  <span className="legend-box unanswered"></span> Unanswered
                </span>
                <span>
                  <span className="legend-box current"></span> Current
                </span>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>

      {/* Submit Confirmation Modal */}
      <Modal
        show={showSubmitModal}
        onHide={() => setShowSubmitModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Submit Test</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to submit your test?</p>
          <Alert variant="warning">
            <strong>Summary:</strong>
            <br />
            Total Questions: {questions.length}
            <br />
            Answered: {Object.keys(answers).length}
            <br />
            Unanswered: {questions.length - Object.keys(answers).length}
          </Alert>
          <p className="text-danger">
            You cannot change your answers after submission.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSubmitModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Submitting..." : "Submit Test"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TakeTest;